import { CancelRounded, Check, Clear, Help, RemoveCircle, Search } from '@mui/icons-material';
import { Pagination } from '@mui/lab';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    LinearProgress,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clearError, getProcesses } from '../../slices/processMonitor';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import ErrorSnackbar from '../errorSnackbar';
import styles from './style.module.scss';
import TableRowProcess from './tableRow';

const cellWidth = {
    info: '80px',
    sessionId: '260px',
    date: '150px',
    icons: '120px',
};

const ROWS_PER_PAGE_ARRAY = [ 5, 10, 20, 50, 100 ];

interface Props {}

const Processes: React.FC<Props> = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { processes, isLoading, error, total } = useAppSelector((state) => state.processMonitor);
    const [ startDate, setStartDate ] = useState<Dayjs>(dayjs().startOf('day'));
    const [ finishDate, setFinishDate ] = useState<Dayjs>(dayjs().endOf('day'));
    const [ searchTerm, setSearchTerm ] = useState('');
    const [ showOnlyErrors, setShowOnlyErrors ] = useState(false);
    const [ rowsPerPage, setRowsPerPage ] = useState(10);
    const [ pagesCount, setPagesCount ] = useState(1);
    const [ currentPage, setCurrentPage ] = useState(1);

    useEffect(() => {
        dispatch(getProcesses(( currentPage - 1 ) * rowsPerPage, rowsPerPage, startDate.toISOString(), finishDate.toISOString(), showOnlyErrors, searchTerm));
    }, [ currentPage, rowsPerPage, startDate, finishDate, showOnlyErrors, searchTerm ]);

    useEffect(() => {
        if (total) {
            setPagesCount(Math.ceil(total / rowsPerPage));
        }
    }, [ total ]);

    if (error) {
        setTimeout(() => dispatch(clearError()), 4000);
    }

    const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.currentTarget.value);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setRowsPerPage(event.target.value as number);
    };

    const handleStartDateChange = (date: Dayjs | null) => {
        setStartDate(dayjs(date).startOf('day'));
    };

    const handleFinishDateChange = (date: Dayjs | null) => {
        setFinishDate(dayjs(date).startOf('day'));
    };

    const handleShowOnlyErrors = () => {
        setShowOnlyErrors(!showOnlyErrors);
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const onKeyDown = (e) => {
        e.preventDefault();
    };

    return (
        <div className={ styles.root3 }>
            { error && <ErrorSnackbar message={ error } /> }
            <Typography 
                variant="h4" 
                sx={{ 
                    mb: 3, 
                    fontWeight: 700,
                    color: '#2b1f86',
                    background: 'linear-gradient(135deg, #2b1f86 0%, #4531e1 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                }}
            >
                { t('links_processes') || 'Processi' }
            </Typography>
            <Grid 
                container 
                direction="row" 
                alignItems="center" 
                justifyContent="flex-start" 
                className={ styles.flexWrap }
                sx={{ 
                    gap: 1,
                    '& > *': {
                        flex: '0 0 auto'
                    }
                }}
            >
                <LocalizationProvider dateAdapter={ AdapterDayjs }>
                    <DesktopDatePicker
                        label={ t('requests_from') }
                        inputFormat="DD.MM.YYYY"
                        value={ startDate }
                        onChange={ handleStartDateChange }
                        maxDate={ finishDate }
                        className={ styles.filter }
                        renderInput={ (params) => <TextField { ...params } onKeyDown={ onKeyDown } /> }
                    />
                    <DesktopDatePicker
                        label={ t('requests_to') }
                        inputFormat="DD.MM.YYYY"
                        value={ finishDate }
                        onChange={ handleFinishDateChange }
                        minDate={ startDate }
                        className={ styles.filter }
                        renderInput={ (params) => <TextField { ...params } onKeyDown={ onKeyDown } /> }
                    />
                </LocalizationProvider>
                <TextField
                    name="search"
                    onChange={ handleSearchTermChange }
                    type="text"
                    label={ t('requests_search') }
                    value={ searchTerm }
                    className={ styles.filter }
                    InputProps={ {
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="primary" />
                            </InputAdornment>
                        ),
                    } }
                />
                <FormControlLabel
                    control={ <Checkbox checked={ showOnlyErrors } onChange={ handleShowOnlyErrors } name="checkedA" /> }
                    label={ t('processes_erroronly') }
                    className={ styles.filter }
                />
                <Pagination count={ pagesCount } page={ currentPage } onChange={ handlePageChange } color="primary" className={ styles.filter } />
                <FormControl variant="outlined" className={ styles.filter }>
                    <TextField select label={ t('grid_rowsperpage') } value={ rowsPerPage } onChange={ handleRowsPerPageChange }>
                        { ROWS_PER_PAGE_ARRAY.map((rowsPerPage, i) => (
                            <MenuItem key={ i } value={ rowsPerPage }>
                                { rowsPerPage }
                            </MenuItem>
                        )) }
                    </TextField>
                </FormControl>
            </Grid>
            <TableContainer component={ Paper } elevation={ 3 } className={ styles.tableContainer }>
                { isLoading && <LinearProgress color="primary" /> }
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell align="left" width={ cellWidth.info }>
                                Info
                            </TableCell>
                            <TableCell align="center" width={ cellWidth.sessionId }>
                                ID
                            </TableCell>
                            <TableCell align="right" width={ cellWidth.date }>
                                { t('date') }
                            </TableCell>
                            <TableCell align="center" width={ cellWidth.icons }>
                                TOS Service
                            </TableCell>
                            <TableCell align="center" width={ cellWidth.icons }>
                                Identyum Service
                            </TableCell>
                            <TableCell align="center" width={ cellWidth.icons }>
                                Blacklist Checker
                            </TableCell>
                            <TableCell align="center" width={ cellWidth.icons }>
                                PSD2 Service
                            </TableCell>
                            <TableCell align="center" width={ cellWidth.icons }>
                                AML Questionnaire
                            </TableCell>
                            <TableCell align="center" width={ cellWidth.icons }>
                                Loan Configurator
                            </TableCell>
                            <TableCell align="center" width={ cellWidth.icons }>
                                Documents
                            </TableCell>
                            <TableCell align="center" width={ cellWidth.icons }>
                                Analytics
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        { processes?.map((process, i) => (
                            <TableRowProcess key={ i } proc={ process } />
                        )) }
                    </TableBody>
                </Table>
            </TableContainer>
            <Grid 
                container 
                direction="row" 
                alignItems="center" 
                mt={ 4 } 
                mb={ 2 }
                sx={{
                    background: '#ffffff',
                    padding: '1.5rem',
                    borderRadius: '16px',
                    boxShadow: '0 2px 16px rgba(0, 0, 0, 0.08)',
                    gap: 3,
                }}
            >
                <Grid 
                    container 
                    direction="row" 
                    alignItems="center" 
                    width="fit-content"
                    sx={{ gap: 1 }}
                >
                    <Clear sx={{ color: '#718096' }} /> 
                    <span style={{ color: '#4d4d4d', fontSize: '0.875rem' }}>Nije započeto</span>
                </Grid>
                <Grid 
                    container 
                    direction="row" 
                    alignItems="center" 
                    width="fit-content"
                    sx={{ gap: 1 }}
                >
                    <Help sx={{ color: '#f59e0b' }} /> 
                    <span style={{ color: '#4d4d4d', fontSize: '0.875rem' }}>Nepoznat status</span>
                </Grid>
                <Grid 
                    container 
                    direction="row" 
                    alignItems="center" 
                    width="fit-content"
                    sx={{ gap: 1 }}
                >
                    <Check sx={{ color: '#10b981' }} /> 
                    <span style={{ color: '#4d4d4d', fontSize: '0.875rem' }}>Uspješno</span>
                </Grid>
                <Grid 
                    container 
                    direction="row" 
                    alignItems="center" 
                    width="fit-content"
                    sx={{ gap: 1 }}
                >
                    <RemoveCircle sx={{ color: '#f59e0b' }} /> 
                    <span style={{ color: '#4d4d4d', fontSize: '0.875rem' }}>Nije završeno</span>
                </Grid>
                <Grid 
                    container 
                    direction="row" 
                    alignItems="center" 
                    width="fit-content"
                    sx={{ gap: 1 }}
                >
                    <CancelRounded sx={{ color: '#ef4444' }} /> 
                    <span style={{ color: '#4d4d4d', fontSize: '0.875rem' }}>Greške</span>
                </Grid>
            </Grid>
        </div>
    );
};

export default Processes;
